/**
 * HousingTopViewHUD.ts
 * 
 * Top-Down 90-degree Orthographic Grid View (평면도 직교 뷰) Modal & Canvas for Housing:
 * - 90-degree perfect orthogonal square tiles (no diamond distortion)
 * - Box Drag Selection (시작점 클릭 ➔ 끝점 드래그로 사각 영역 일괄 지정/칠하기)
 * - Drag painting with Tab [그리기 / 지우기] toggle
 * - Visualizes ownership, walls, floors, furniture, facilities
 * - Seamless sync with HousingSystem and IsoRenderer
 */

import { housingSystem } from '../world/HousingSystem';
import { housingCatalog } from '../world/HousingCatalog';
import { housingModeController } from './HousingModeController';
import { EntityRegistry } from '../../core/EntityRegistry';
import { getZoneData } from '../world/ZoneMapData';
import { Currency } from '../../core/Currency';
import { EventBus } from '../../core/EventBus';

export class HousingTopViewHUD {
  private static instance: HousingTopViewHUD;

  public isOpen = false;
  private containerEl: HTMLElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;

  // Camera in Top-Down view (center tile coordinates)
  public camTileX = 80;
  public camTileY = 80;
  public tileSize = 24; // 24px per tile

  // Box drag selection
  private isMouseDown = false;
  private dragStartTile: { x: number; y: number } | null = null;
  private dragCurrentTile: { x: number; y: number } | null = null;

  private registry: EntityRegistry | null = null;
  private getZoneIdCb: (() => number) | null = null;
  private isAdminCb: (() => boolean) | null = null;

  public static getInstance(): HousingTopViewHUD {
    if (!HousingTopViewHUD.instance) {
      HousingTopViewHUD.instance = new HousingTopViewHUD();
    }
    return HousingTopViewHUD.instance;
  }

  public init(
    registry: EntityRegistry,
    getZoneId: () => number,
    isAdmin: () => boolean
  ): void {
    this.registry = registry;
    this.getZoneIdCb = getZoneId;
    this.isAdminCb = isAdmin;

    this.buildDOM();
    this.bindEvents();
  }

  public open(): void {
    this.isOpen = true;
    const player = this.registry?.getPlayer();
    if (player) {
      this.camTileX = Math.round(player.x);
      this.camTileY = Math.round(player.y);
    }
    if (this.containerEl) {
      this.containerEl.style.display = 'flex';
      this.resizeCanvas();
    }
    this.render();
  }

  public close(): void {
    this.isOpen = false;
    if (this.containerEl) {
      this.containerEl.style.display = 'none';
    }
  }

  public toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  private buildDOM(): void {
    this.containerEl = document.createElement('div');
    this.containerEl.id = 'housing-topview-modal';
    this.containerEl.style.cssText = `
      position: fixed;
      inset: 20px 40px;
      background: rgba(10, 15, 29, 0.96);
      border: 2px solid #38bdf8;
      border-radius: 12px;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.9);
      z-index: 10050;
      display: none;
      flex-direction: column;
      font-family: 'Pretendard', sans-serif;
      backdrop-filter: blur(10px);
      user-select: none;
      overflow: hidden;
    `;

    this.containerEl.innerHTML = `
      <!-- TOP VIEW HEADER -->
      <div style="background: #0f172a; padding: 10px 16px; display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #1e293b;">
        <div style="display: flex; align-items: center; gap: 12px;">
          <span style="font-weight: 800; font-size: 1.05rem; color: #38bdf8; display: flex; align-items: center; gap: 6px;">
            📐 하우징 직교 평면도 뷰 (Top-Down 90°)
          </span>
          <span style="font-size: 0.78rem; color: #94a3b8;">
            직사각형 바둑판 격자로 왜곡 없이 반듯하게 건축 구역과 타일을 배치합니다.
          </span>
        </div>
        <div style="display: flex; align-items: center; gap: 10px;">
          <span id="topview-coord-info" style="font-size: 0.8rem; color: #fde047; font-weight: bold;">중심: (80, 80)</span>
          <button id="topview-btn-toggle-brush" style="background: #16a34a; color: #fff; border: none; padding: 4px 10px; border-radius: 4px; font-weight: bold; font-size: 0.75rem; cursor: pointer;">
            🖌️ 그리기 모드 (TAB)
          </button>
          <button id="topview-btn-close" style="background: #ef4444; color: #fff; border: none; padding: 4px 12px; border-radius: 4px; font-weight: bold; font-size: 0.8rem; cursor: pointer;">
            닫기 (V / ESC)
          </button>
        </div>
      </div>

      <!-- CANVAS AREA -->
      <div id="topview-canvas-wrapper" style="flex: 1; position: relative; overflow: hidden; background: #070a13; cursor: crosshair;">
        <canvas id="topview-canvas" style="display: block; width: 100%; height: 100%;"></canvas>
      </div>

      <!-- BOTTOM CONTROL BAR -->
      <div style="background: #090d16; padding: 8px 16px; display: flex; justify-content: space-between; align-items: center; font-size: 0.75rem; color: #cbd5e1; border-top: 1px solid #1e293b;">
        <div>
          <b>마우스 드래그</b>: 사각 영역(Box) 일괄 선택/설치 | <b>휠 스크롤</b>: 확대/축소 | <b>우클릭 드래그</b>: 화면 이동 | <b>TAB</b>: [그리기 ↔ 지우기]
        </div>
        <div id="topview-selection-info" style="color: #38bdf8; font-weight: bold;">
          선택 영역: 0 칸
        </div>
      </div>
    `;

    document.body.appendChild(this.containerEl);

    this.canvas = this.containerEl.querySelector('#topview-canvas') as HTMLCanvasElement;
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d');
    }
  }

  private resizeCanvas(): void {
    const wrapper = this.containerEl?.querySelector('#topview-canvas-wrapper') as HTMLElement | null;
    if (wrapper && this.canvas) {
      this.canvas.width = wrapper.clientWidth;
      this.canvas.height = wrapper.clientHeight;
    }
  }

  private bindEvents(): void {
    if (!this.containerEl || !this.canvas) return;

    window.addEventListener('resize', () => {
      if (this.isOpen) {
        this.resizeCanvas();
        this.render();
      }
    });

    // Close buttons
    this.containerEl.querySelector('#topview-btn-close')?.addEventListener('click', () => this.close());

    // Toggle brush mode button
    const brushToggleBtn = this.containerEl.querySelector('#topview-btn-toggle-brush') as HTMLButtonElement | null;
    brushToggleBtn?.addEventListener('click', () => {
      housingModeController.brushMode = housingModeController.brushMode === 'draw' ? 'erase' : 'draw';
      this.updateBrushButton();
      this.render();
    });

    // Keyboard V and ESC
    window.addEventListener('keydown', (e) => {
      if (!housingModeController.isHousingActive()) return;
      if (e.key === 'v' || e.key === 'V') {
        e.preventDefault();
        this.toggle();
      } else if (e.key === 'Escape' && this.isOpen) {
        e.preventDefault();
        this.close();
      } else if (e.key === 'Tab' && this.isOpen) {
        e.preventDefault();
        housingModeController.brushMode = housingModeController.brushMode === 'draw' ? 'erase' : 'draw';
        this.updateBrushButton();
        this.render();
      }
    });

    // Mouse Controls on Canvas (Box Drag, Pan, Zoom)
    let isPanning = false;
    let panStartX = 0, panStartY = 0;

    this.canvas.addEventListener('mousedown', (e) => {
      const rect = this.canvas!.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const tile = this.screenToTile(mx, my);

      if (e.button === 0) {
        // Left click: start box drag
        this.isMouseDown = true;
        this.dragStartTile = tile;
        this.dragCurrentTile = tile;
        this.render();
      } else if (e.button === 2) {
        // Right click: start panning
        isPanning = true;
        panStartX = e.clientX;
        panStartY = e.clientY;
      }
    });

    window.addEventListener('mousemove', (e) => {
      if (!this.isOpen || !this.canvas) return;

      if (isPanning) {
        const dx = e.clientX - panStartX;
        const dy = e.clientY - panStartY;
        panStartX = e.clientX;
        panStartY = e.clientY;
        this.camTileX -= dx / this.tileSize;
        this.camTileY -= dy / this.tileSize;
        this.render();
        return;
      }

      if (this.isMouseDown && this.dragStartTile) {
        const rect = this.canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        this.dragCurrentTile = this.screenToTile(mx, my);
        this.render();
      }
    });

    window.addEventListener('mouseup', (e) => {
      if (isPanning && e.button === 2) {
        isPanning = false;
      }

      if (this.isMouseDown && e.button === 0) {
        this.isMouseDown = false;
        if (this.dragStartTile && this.dragCurrentTile) {
          this.applyBoxAction(this.dragStartTile, this.dragCurrentTile);
        }
        this.dragStartTile = null;
        this.dragCurrentTile = null;
        this.render();
      }
    });

    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    // Mouse Wheel Zoom
    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        this.tileSize = Math.min(64, this.tileSize + 4);
      } else {
        this.tileSize = Math.max(12, this.tileSize - 4);
      }
      this.render();
    });
  }

  private updateBrushButton(): void {
    const btn = this.containerEl?.querySelector('#topview-btn-toggle-brush') as HTMLButtonElement | null;
    if (btn) {
      const isDraw = housingModeController.brushMode === 'draw';
      btn.style.background = isDraw ? '#16a34a' : '#dc2626';
      btn.innerText = isDraw ? '🖌️ 그리기 모드 (TAB)' : '🧹 지우기 모드 (TAB)';
    }
  }

  private screenToTile(sx: number, sy: number): { x: number; y: number } {
    const cx = this.canvas!.width / 2;
    const cy = this.canvas!.height / 2;
    const tx = Math.floor((sx - cx) / this.tileSize + this.camTileX);
    const ty = Math.floor((sy - cy) / this.tileSize + this.camTileY);
    return { x: tx, y: ty };
  }

  private tileToScreen(tx: number, ty: number): { x: number; y: number } {
    const cx = this.canvas!.width / 2;
    const cy = this.canvas!.height / 2;
    const sx = (tx - this.camTileX) * this.tileSize + cx;
    const sy = (ty - this.camTileY) * this.tileSize + cy;
    return { x: sx, y: sy };
  }

  /**
   * Apply Batch Box Action (Drag Box Fill or Erase)
   */
  private applyBoxAction(p1: { x: number; y: number }, p2: { x: number; y: number }): void {
    const minX = Math.min(p1.x, p2.x);
    const maxX = Math.max(p1.x, p2.x);
    const minY = Math.min(p1.y, p2.y);
    const maxY = Math.max(p1.y, p2.y);

    const zoneId = this.getZoneIdCb ? this.getZoneIdCb() : 1;
    const player = this.registry?.getPlayer();
    const isAdmin = this.isAdminCb ? this.isAdminCb() : false;
    if (!player) return;

    let count = 0;
    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        housingModeController.applyPaintAt(tx, ty);
        count++;
      }
    }

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: Date.now(),
      text: `📐 [평면도 일괄 ${housingModeController.brushMode === 'draw' ? '배치' : '삭제'}] 총 ${count}칸 영역 작업 완료!`,
      category: 'system',
    });
  }

  // ==========================================================================
  // RENDER TOP-DOWN 90 DEGREE ORTHOGONAL GRID
  // ==========================================================================

  public render(): void {
    if (!this.isOpen || !this.ctx || !this.canvas) return;
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const zoneId = this.getZoneIdCb ? this.getZoneIdCb() : 1;
    const zone = getZoneData(zoneId);

    ctx.fillStyle = '#060913';
    ctx.fillRect(0, 0, w, h);

    const halfCols = Math.ceil(w / this.tileSize / 2) + 2;
    const halfRows = Math.ceil(h / this.tileSize / 2) + 2;
    const minTX = Math.max(0, Math.floor(this.camTileX - halfCols));
    const maxTX = Math.min(zone.width - 1, Math.ceil(this.camTileX + halfCols));
    const minTY = Math.max(0, Math.floor(this.camTileY - halfRows));
    const maxTY = Math.min(zone.height - 1, Math.ceil(this.camTileY + halfRows));

    // 1. Draw 90-degree Grid Base Tiles
    for (let ty = minTY; ty <= maxTY; ty++) {
      for (let tx = minTX; tx <= maxTX; tx++) {
        const pt = this.tileToScreen(tx, ty);
        const tileType = zone.tiles[ty]?.[tx] || 'grass';

        // Base tile colors
        if (tileType === 'cobblestone') ctx.fillStyle = '#64748b';
        else if (tileType === 'dirt_road') ctx.fillStyle = '#92400e';
        else if (tileType === 'water') ctx.fillStyle = '#0284c7';
        else if (tileType === 'stone_wall') ctx.fillStyle = '#334155';
        else if (tileType === 'hills_grass') ctx.fillStyle = '#15803d';
        else ctx.fillStyle = (tx + ty) % 2 === 0 ? '#14532d' : '#166534';

        ctx.fillRect(pt.x, pt.y, this.tileSize, this.tileSize);

        // Thin grid border
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
        ctx.lineWidth = 1;
        ctx.strokeRect(pt.x, pt.y, this.tileSize, this.tileSize);
      }
    }

    // 2. Draw Owned Land Highlights
    for (let ty = minTY; ty <= maxTY; ty++) {
      for (let tx = minTX; tx <= maxTX; tx++) {
        const owner = housingSystem.getLandOwner(zoneId, tx, ty);
        if (owner) {
          const pt = this.tileToScreen(tx, ty);
          ctx.fillStyle = 'rgba(56, 189, 248, 0.25)';
          ctx.fillRect(pt.x, pt.y, this.tileSize, this.tileSize);
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = 1.5;
          ctx.strokeRect(pt.x + 1, pt.y + 1, this.tileSize - 2, this.tileSize - 2);
        }
      }
    }

    // 3. Draw Placed Housing Objects
    const placedList = housingSystem.getPlacedObjectsInZone(zoneId);
    for (const obj of placedList) {
      if (obj.x >= minTX && obj.x <= maxTX && obj.y >= minTY && obj.y <= maxTY) {
        const pt = this.tileToScreen(obj.x, obj.y);
        const item = housingCatalog.get(obj.catalogId);
        if (item) {
          item.draw(ctx, pt.x + this.tileSize / 2, pt.y + this.tileSize / 2, this.tileSize, obj.rotation);
        }
      }
    }

    // 4. Draw Currently Selected Claim Tiles
    if (housingModeController.selectedTab === 'claim') {
      for (const t of housingModeController.claimSelectedTiles.values()) {
        const pt = this.tileToScreen(t.x, t.y);
        ctx.fillStyle = 'rgba(250, 204, 21, 0.45)';
        ctx.fillRect(pt.x, pt.y, this.tileSize, this.tileSize);
        ctx.strokeStyle = '#facc15';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(pt.x, pt.y, this.tileSize, this.tileSize);
      }
    }

    // 5. Draw Box Drag Selection Preview
    if (this.isMouseDown && this.dragStartTile && this.dragCurrentTile) {
      const minX = Math.min(this.dragStartTile.x, this.dragCurrentTile.x);
      const maxX = Math.max(this.dragStartTile.x, this.dragCurrentTile.x);
      const minY = Math.min(this.dragStartTile.y, this.dragCurrentTile.y);
      const maxY = Math.max(this.dragStartTile.y, this.dragCurrentTile.y);

      const p1 = this.tileToScreen(minX, minY);
      const p2 = this.tileToScreen(maxX + 1, maxY + 1);
      const boxW = p2.x - p1.x;
      const boxH = p2.y - p1.y;

      const isDraw = housingModeController.brushMode === 'draw';
      ctx.fillStyle = isDraw ? 'rgba(56, 189, 248, 0.35)' : 'rgba(239, 68, 68, 0.4)';
      ctx.fillRect(p1.x, p1.y, boxW, boxH);
      ctx.strokeStyle = isDraw ? '#38bdf8' : '#ef4444';
      ctx.lineWidth = 2;
      ctx.strokeRect(p1.x, p1.y, boxW, boxH);

      const totalTiles = (maxX - minX + 1) * (maxY - minY + 1);
      const infoEl = this.containerEl?.querySelector('#topview-selection-info');
      if (infoEl) {
        infoEl.innerHTML = `선택 영역: <b>${maxX - minX + 1} × ${maxY - minY + 1} = ${totalTiles} 칸</b> (${isDraw ? '설치/지정' : '삭제'})`;
      }
    }

    // Update Coordinate Info
    const coordEl = this.containerEl?.querySelector('#topview-coord-info') as HTMLElement | null;
    if (coordEl) {
      coordEl.innerText = `중심 좌표: (${Math.round(this.camTileX)}, ${Math.round(this.camTileY)}) | 배율: ${this.tileSize}px`;
    }
  }
}

export const housingTopViewHUD = HousingTopViewHUD.getInstance();
